import type { BrowserContext, Page } from 'playwright';
import type { BrowserLogger } from '../logger.js';
import { resolveBrowserContextRequestUrl } from './request-url.js';

/**
 * Opt-in egress policy for model-authored `run_code` blocks (set per client via
 * `setRunCodeEgressPolicy`). With a policy armed:
 * - the injected `fetch` binding (worker-side!) refuses non-http(s) URLs and
 *   hosts outside the allowlist;
 * - `page.goto` refuses non-http(s) schemes (`file:`, `data:`) and off-host
 *   targets at the binding surface;
 * - `page.context()` is denied outright (it reaches `unrouteAll`/
 *   `storageState`, which would disarm the network host boundary or dump the
 *   authenticated state).
 * This complements the context-route host boundary (which owns http(s)
 * navigations in every frame); deep Playwright surfaces reachable through
 * frames remain — callers must state that residual honestly, not rely on this
 * as a complete sandbox.
 */
export type RunCodeEgressPolicy = {
  allowedHosts: string[];
};

const ensurePolicyAllowsUrl = (
  rawUrl: string,
  base: string | undefined,
  policy: RunCodeEgressPolicy,
  surface: string
): string => {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl, base);
  } catch {
    throw new Error(`${surface} blocked: "${rawUrl}" is not a valid URL.`);
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(
      `${surface} blocked: only http(s) URLs are allowed in this session (got ${parsed.protocol}).`
    );
  }
  if (!policy.allowedHosts.includes(parsed.host)) {
    throw new Error(
      `${surface} blocked: ${parsed.host} is outside this session's allowed hosts ` +
        `(${policy.allowedHosts.join(', ')}).`
    );
  }
  return parsed.toString();
};

const fetchInputUrl = (input: unknown): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (typeof input === 'object' && input !== null && 'url' in input) {
    const url = (input as { url: unknown }).url;
    if (typeof url === 'string') return url;
  }
  return String(input);
};

/**
 * Minimal EventEmitter shape for Page. Playwright's Page extends Node's
 * EventEmitter at runtime but its public types only expose typed `on/off`
 * overloads per event; `rawListeners` and generic `off` aren't typed. We cast
 * through this shape to reach them without `any`.
 */
type EventEmitterShape = {
  rawListeners(event: string): Array<(...args: unknown[]) => void>;
  off(event: string, listener: (...args: unknown[]) => void): unknown;
};

export function asEmitter(page: Page): EventEmitterShape {
  return page as unknown as EventEmitterShape;
}

/**
 * Remove any listeners on `page[event]` that weren't in `baseline` (in
 * registration order). Returns the count removed. Used by runCode to prevent
 * agent-authored `page.on('dialog', …)` from leaking into subsequent tool
 * calls and racing our global handler.
 */
export function stripNewListeners(
  page: Page,
  event: 'dialog' | 'download' | 'filechooser',
  baseline: ReadonlyArray<(...args: unknown[]) => void>
): number {
  const emitter = page as unknown as Partial<EventEmitterShape>;
  // Defensive: Playwright's Page extends Node's EventEmitter at runtime but
  // that's an implementation detail. If a future Playwright version changes
  // this, degrade gracefully instead of throwing mid-tool-call.
  if (typeof emitter.rawListeners !== 'function' || typeof emitter.off !== 'function') {
    return 0;
  }
  const baselineSet = new Set(baseline);
  const current = emitter.rawListeners(event);
  let stripped = 0;
  for (const listener of current) {
    if (!baselineSet.has(listener)) {
      emitter.off(event, listener);
      stripped++;
    }
  }
  return stripped;
}

type WaitForEventTarget = {
  waitForEvent: (...args: unknown[]) => Promise<unknown>;
};

export type RunCodeWaitGuardTarget = WaitForEventTarget & {
  waitForRequest?: (...args: unknown[]) => Promise<unknown>;
  waitForResponse?: (...args: unknown[]) => Promise<unknown>;
};

export type RunCodeWaitGuardCounts = {
  fileChooser: number;
  request: number;
  response: number;
};

export type RunCodeFetch = (
  ...args: Parameters<typeof globalThis.fetch>
) => ReturnType<typeof globalThis.fetch>;

type RunCodePageRequestMethod = 'delete' | 'fetch' | 'get' | 'head' | 'patch' | 'post' | 'put';

const runCodePageRequestMethods = new Set<PropertyKey>([
  'delete',
  'fetch',
  'get',
  'head',
  'patch',
  'post',
  'put',
]);

function logLateRunCodeWaitRejection(
  logger: Pick<BrowserLogger, 'debug'>,
  kind: 'filechooser' | 'request' | 'response',
  promise: Promise<unknown>
): void {
  promise.catch((err) => {
    logger.debug(`[DirectPlaywright] runCode ${kind} wait rejected after call`, {
      error: err instanceof Error ? err.message : String(err),
    });
  });
}

export function guardRunCodeFetch(
  logger: Pick<BrowserLogger, 'debug'>,
  policy?: RunCodeEgressPolicy
): RunCodeFetch {
  return (...args: Parameters<RunCodeFetch>): ReturnType<RunCodeFetch> => {
    if (policy) {
      // This fetch runs in the WORKER process — without the policy it is
      // unrestricted server-side egress that trivially outruns the browser
      // navigation guard. Fail loud (thrown into the model's code result).
      ensurePolicyAllowsUrl(fetchInputUrl(args[0]), undefined, policy, 'fetch');
    }
    const raw = globalThis.fetch(...args);
    const promise = policy
      ? raw.then((response) => {
          // fetch follows redirects — re-validate where the request LANDED so
          // an allowed host cannot 3xx the worker off the allowlist.
          ensurePolicyAllowsUrl(response.url, undefined, policy, 'fetch (after redirects)');
          return response;
        })
      : raw;
    promise.catch((err) => {
      logger.debug('[DirectPlaywright] runCode fetch rejected after call', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
    return promise;
  };
}

function isPromiseLike(value: unknown): value is Promise<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { catch?: unknown }).catch === 'function'
  );
}

function logLateRunCodePageRequestRejection(
  logger: Pick<BrowserLogger, 'debug'>,
  method: RunCodePageRequestMethod,
  url: string,
  promise: Promise<unknown>
): void {
  promise.catch((err) => {
    logger.debug('[DirectPlaywright] runCode page.request rejected after call', {
      method,
      url: url.slice(0, 200),
      error: err instanceof Error ? err.message : String(err),
    });
  });
}

function guardRunCodePageRequestContext(
  request: unknown,
  getPageUrl: () => string | null,
  logger: Pick<BrowserLogger, 'debug'>,
  policy?: RunCodeEgressPolicy
): unknown {
  if (request === null || (typeof request !== 'object' && typeof request !== 'function')) {
    return request;
  }

  const functionCache = new Map<PropertyKey, unknown>();
  let requestProxySelf: object | null = null;
  const requestProxy = new Proxy(request as object, {
    ...(policy ? { getPrototypeOf: () => null } : {}),
    get(target, property, receiver) {
      if (policy && (property === 'constructor' || property === '__proto__')) {
        return undefined;
      }
      // page.request carries the context's cookies — under an egress policy,
      // storageState would dump the authenticated state driver-side.
      if (policy && property === 'storageState') {
        const denied = () => {
          throw new Error('request.storageState() is not available in this session.');
        };
        functionCache.set(property, denied);
        return denied;
      }
      const value = Reflect.get(target, property, receiver);
      if (typeof value !== 'function') {
        return value;
      }

      const cached = functionCache.get(property);
      if (cached) {
        return cached;
      }

      if (!runCodePageRequestMethods.has(property)) {
        if (policy) {
          // Same returns-`this` leak closure as the page proxy (valueOf etc.).
          const guarded = (...args: unknown[]) => {
            const result = value.apply(target, args) as unknown;
            if (result === (target as unknown)) return requestProxySelf ?? result;
            if (isPromiseLike(result)) {
              return (result as Promise<unknown>).then((resolved) =>
                resolved === (target as unknown) ? (requestProxySelf ?? resolved) : resolved
              );
            }
            return result;
          };
          functionCache.set(property, guarded);
          return guarded;
        }
        const bound = value.bind(target);
        functionCache.set(property, bound);
        return bound;
      }

      const method = property as RunCodePageRequestMethod;
      const guarded = (...args: unknown[]) => {
        const requestArgs = [...args];
        const originalUrl = requestArgs[0];
        let requestUrl: string | null = null;
        if (typeof originalUrl === 'string') {
          requestUrl = resolveBrowserContextRequestUrl(originalUrl, getPageUrl());
          requestArgs[0] = requestUrl;
        }

        if (policy) {
          // Credentialed driver-side egress (APIRequestContext sends the
          // context's cookies) — same policy gate as the fetch binding.
          const checked = ensurePolicyAllowsUrl(
            requestUrl ?? fetchInputUrl(originalUrl),
            getPageUrl() ?? undefined,
            policy,
            `page.request.${String(method)}`
          );
          if (requestUrl !== null) requestArgs[0] = checked;
        }

        const result = value.apply(target, requestArgs) as unknown;
        if (isPromiseLike(result) && requestUrl !== null) {
          logLateRunCodePageRequestRejection(logger, method, requestUrl, result);
        }
        return result;
      };
      functionCache.set(property, guarded);
      return guarded;
    },
  });
  requestProxySelf = requestProxy;
  return requestProxy;
}

function guardRunCodeBrowserContextRequest(
  context: BrowserContext,
  getPageUrl: () => string | null,
  logger: Pick<BrowserLogger, 'debug'>
): BrowserContext {
  const guardedRequest = guardRunCodePageRequestContext(context.request, getPageUrl, logger);
  const functionCache = new Map<PropertyKey, unknown>();

  return new Proxy(context as unknown as object, {
    get(target, property, receiver) {
      if (property === 'request') {
        return guardedRequest;
      }

      const value = Reflect.get(target, property, receiver);
      if (typeof value !== 'function') {
        return value;
      }

      const cached = functionCache.get(property);
      if (cached) {
        return cached;
      }

      const bound = value.bind(context);
      functionCache.set(property, bound);
      return bound;
    },
  }) as unknown as BrowserContext;
}

export function guardRunCodePageRequest(
  page: Page,
  logger: Pick<BrowserLogger, 'debug'>,
  policy?: RunCodeEgressPolicy
): Page {
  // runCode exposes the raw Playwright page. Guard the diagnostic-only
  // request surfaces so they inherit the same relative URL handling as the
  // first-class browser fetch helpers.
  const guardedRequest = guardRunCodePageRequestContext(
    page.request,
    () => page.url(),
    logger,
    policy
  );
  const functionCache = new Map<PropertyKey, unknown>();
  const contextCache = new WeakMap<object, BrowserContext>();

  // Self-reference for the returns-`this` leak closure below (assigned right
  // after the Proxy is constructed; only read at call time).
  let proxySelf: object | null = null;
  const proxy = new Proxy(page as unknown as object, {
    // Under a policy, seal the prototype chain: Object.getPrototypeOf(page)
    // would hand back the REAL Page.prototype, whose unguarded goto/context
    // could then be invoked with the proxy as `this` — a one-line bypass of
    // every per-property denial below.
    ...(policy ? { getPrototypeOf: () => null } : {}),
    get(target, property, receiver) {
      if (policy && (property === 'constructor' || property === '__proto__')) {
        // Both leak the real prototype (constructor.prototype / __proto__).
        return undefined;
      }
      if (property === 'request') {
        return guardedRequest;
      }

      // Route manipulation from model code would disarm the context-level host
      // boundary (page routes take precedence over context routes, and a
      // catch-all continue() finalizes instead of falling through). Deny.
      if (
        policy &&
        (property === 'route' || property === 'unroute' || property === 'unrouteAll' ||
          property === 'routeFromHAR' || property === 'routeWebSocket')
      ) {
        const denied = () => {
          throw new Error(`page.${String(property)}() is not available in this session.`);
        };
        return denied;
      }

      const value = Reflect.get(target, property, receiver);
      if (typeof value !== 'function') {
        return value;
      }

      const cached = functionCache.get(property);
      if (cached) {
        return cached;
      }

      if (policy && property === 'goto') {
        const guarded = (url: unknown, ...rest: unknown[]) => {
          const resolved = ensurePolicyAllowsUrl(String(url), page.url(), policy, 'page.goto');
          return value.apply(page, [resolved, ...rest]);
        };
        functionCache.set(property, guarded);
        return guarded;
      }

      if (policy && property === 'context') {
        // context reaches unrouteAll (disarms the network host boundary) and
        // storageState (dumps the authenticated cookies). Deny outright.
        const guarded = () => {
          throw new Error('page.context() is not available in this session.');
        };
        functionCache.set(property, guarded);
        return guarded;
      }

      if (property === 'context') {
        const guarded = (...args: unknown[]) => {
          const context = value.apply(page, args) as unknown;
          if (context === null || (typeof context !== 'object' && typeof context !== 'function')) {
            return context;
          }

          const contextKey = context as object;
          const cachedContext = contextCache.get(contextKey);
          if (cachedContext) {
            return cachedContext;
          }

          const guardedContext = guardRunCodeBrowserContextRequest(
            context as BrowserContext,
            () => page.url(),
            logger
          );
          contextCache.set(contextKey, guardedContext);
          return guardedContext;
        };
        functionCache.set(property, guarded);
        return guarded;
      }

      if (policy) {
        // Generic fallthrough under a policy: any inherited or chaining method
        // that returns `this` (Object.prototype.valueOf, EventEmitter's
        // on/off/once) would hand back the RAW page, defeating every denial
        // above in one call. Substitute the proxy whenever the real target
        // escapes as a return value (sync or via promise).
        const guarded = (...args: unknown[]) => {
          const result = value.apply(page, args) as unknown;
          if (result === (page as unknown)) return proxySelf ?? result;
          if (isPromiseLike(result)) {
            return (result as Promise<unknown>).then((resolved) =>
              resolved === (page as unknown) ? (proxySelf ?? resolved) : resolved
            );
          }
          return result;
        };
        functionCache.set(property, guarded);
        return guarded;
      }

      const bound = value.bind(page);
      functionCache.set(property, bound);
      return bound;
    },
  }) as unknown as Page;
  proxySelf = proxy as unknown as object;
  return proxy;
}

export function guardFileChooserWaitForEvent(
  page: WaitForEventTarget,
  logger: Pick<BrowserLogger, 'debug'>
): () => number {
  const originalWaitForEvent = page.waitForEvent;
  let guarded = 0;

  page.waitForEvent = (...args: unknown[]) => {
    const promise = originalWaitForEvent.call(page, ...args);
    if (args[0] === 'filechooser') {
      guarded += 1;
      logLateRunCodeWaitRejection(logger, 'filechooser', promise);
    }
    return promise;
  };

  return () => {
    page.waitForEvent = originalWaitForEvent;
    return guarded;
  };
}

export function guardRunCodeWaits(
  page: RunCodeWaitGuardTarget,
  logger: Pick<BrowserLogger, 'debug'>
): () => RunCodeWaitGuardCounts {
  const restoreWaitForEvent = guardFileChooserWaitForEvent(page, logger);
  const originalWaitForRequest = page.waitForRequest;
  const originalWaitForResponse = page.waitForResponse;
  let guardedRequests = 0;
  let guardedResponses = 0;

  if (typeof originalWaitForRequest === 'function') {
    page.waitForRequest = (...args: unknown[]) => {
      const promise = originalWaitForRequest.call(page, ...args);
      guardedRequests += 1;
      logLateRunCodeWaitRejection(logger, 'request', promise);
      return promise;
    };
  }

  if (typeof originalWaitForResponse === 'function') {
    page.waitForResponse = (...args: unknown[]) => {
      const promise = originalWaitForResponse.call(page, ...args);
      guardedResponses += 1;
      logLateRunCodeWaitRejection(logger, 'response', promise);
      return promise;
    };
  }

  return () => {
    const fileChooser = restoreWaitForEvent();
    if (typeof originalWaitForRequest === 'function') {
      page.waitForRequest = originalWaitForRequest;
    }
    if (typeof originalWaitForResponse === 'function') {
      page.waitForResponse = originalWaitForResponse;
    }
    return {
      fileChooser,
      request: guardedRequests,
      response: guardedResponses,
    };
  };
}

/**
 * A named function DECLARATION — `function clickSave(`, `async function clickSave(`.
 * At statement position this is a statement, not an expression, and is routinely
 * followed by the statements that call it:
 *
 *     async function clickSave() { … }
 *     if (ready) { await clickSave(); }
 *
 * That source executes correctly inside the wrapper, so it must never be rejected.
 * Checked before {@link LEADING_FUNCTION_EXPRESSION}, which would otherwise match
 * the leading `function` keyword.
 */
const LEADING_FUNCTION_DECLARATION = /^(?:async\s+)?function\s+[A-Za-z_$][\w$]*\s*\(/;

/**
 * Leading function-EXPRESSION shapes: `async ({ page }) => `, `(page) => `,
 * `page => `, and ANONYMOUS `function (…)`. Anchored at the start of the trimmed
 * source. A named `function foo(…)` is deliberately excluded — see
 * {@link LEADING_FUNCTION_DECLARATION}.
 */
const LEADING_FUNCTION_EXPRESSION =
  /^(?:async\s+)?(?:function\s*\(|\([^)]*\)\s*=>|[A-Za-z_$][\w$]*\s*=>)/;

/**
 * True when the ENTIRE source parses as a single expression.
 *
 * This is a compile probe: the function is built and thrown away, never invoked,
 * so it has no side effects and cannot run agent code. It is exact where
 * character-sniffing is not — source with statements after the leading function
 * literal fails to parse as one parenthesised expression, which is precisely the
 * "declaration/expression followed by real work" case that must be allowed. The
 * `async` wrapper keeps an `await` inside the expression from raising a spurious
 * SyntaxError.
 */
function isSingleExpression(code: string): boolean {
  try {
    new Function(`async () => { return (\n${code}\n); }`);
    return true;
  } catch {
    return false;
  }
}

const FUNCTION_EXPRESSION_BODY_MESSAGE =
  'run_code takes a statement body, not a function expression. ' +
  'Write the statements directly — `await page.getByRole("button").click();` — ' +
  'not `async ({ page }) => { … }`. `page` and any bindings are already in scope. ' +
  '(Interaction-recipe code IS stored in the function-expression form; that form is ' +
  'wrapped and invoked by the recipe runner, and cannot be pasted into run_code as-is.)';

/**
 * Find the index of the `)` that closes the `(` at `openIndex`, tracking paren
 * depth while skipping over string/template literal contents and comments so
 * that stray brackets inside quoted text or comments don't perturb the count.
 * Returns -1 if the source doesn't balance (shouldn't happen for code that
 * already passed {@link isSingleExpression}, but callers must treat -1 as
 * "can't tell" rather than crash).
 */
function findMatchingParenClose(source: string, openIndex: number): number {
  let depth = 0;
  for (let i = openIndex; i < source.length; i++) {
    const ch = source[i];
    if (ch === '(') {
      depth++;
    } else if (ch === ')') {
      depth--;
      if (depth === 0) return i;
    } else if (ch === '"' || ch === "'" || ch === '`') {
      i = skipStringLiteral(source, i, ch);
    } else if (ch === '/' && source[i + 1] === '/') {
      const newline = source.indexOf('\n', i);
      i = newline === -1 ? source.length : newline;
    } else if (ch === '/' && source[i + 1] === '*') {
      const end = source.indexOf('*/', i + 2);
      i = end === -1 ? source.length : end + 1;
    }
  }
  return -1;
}

/** Returns the index of the closing quote matching `quote` opened at `startIndex`. */
function skipStringLiteral(source: string, startIndex: number, quote: string): number {
  let i = startIndex + 1;
  while (i < source.length) {
    if (source[i] === '\\') {
      i += 2;
      continue;
    }
    if (source[i] === quote) return i;
    i++;
  }
  return source.length - 1;
}

/**
 * Detect a run_code body that is a bare function EXPRESSION rather than a
 * statement body, and return an agent-facing error message (or `null` when the
 * code is fine).
 *
 * {@link executeRunCode} splices `code` into `return (async () => { CODE })()`.
 * So a block written as `async ({ page }) => { … }` — the shape used by
 * interaction recipes, and therefore the shape an agent is most likely to copy —
 * compiles to a function that is *created and immediately discarded*. It reports
 * `ok: true` with nothing executed, which downstream reads as "the action ran and
 * changed nothing." That silent-wrong outcome is what this guard converts into a
 * loud, correctable error.
 *
 * Only source whose ENTIRE body is one discarded function literal is rejected.
 * Three shapes are explicitly allowed because they really do execute:
 *
 *  - a named declaration followed by statements that call it
 *    (`async function clickSave() { … } if (ready) { await clickSave(); }`);
 *  - any function literal followed by further statements, detected by parse
 *    rather than by sniffing the final character;
 *  - an immediately-invoked expression, where the invocation is a real call
 *    applied to the function literal (`(async () => { … })()`).
 *
 * A parenthesised function literal that is merely WRAPPED — `(async ({ page }) =>
 * { … })` with no trailing `(...)` call — also ends in `)`, but is not invoked and
 * is rejected: {@link findMatchingParenClose} walks the leading `(` to its match
 * and checks whether that match is the last character (pure grouping, reject) or
 * has a call suffix after it (real IIFE, allow).
 *
 * The residual gap is a concise-body arrow (`page => page.title()`), which is a
 * single expression ending in `)` and is still accepted. The bias is deliberate:
 * a false rejection is loud and the agent rewrites, while a false acceptance is
 * the silent no-op being fixed.
 */
export function detectFunctionExpressionBody(code: string): string | null {
  const trimmed = code.trim();

  // A named declaration is a statement — executable statements routinely follow.
  if (LEADING_FUNCTION_DECLARATION.test(trimmed)) return null;
  if (!LEADING_FUNCTION_EXPRESSION.test(trimmed)) return null;

  // Trailing semicolons are cosmetic. Strip them before the compile probe too:
  // `(async () => { … })();` embedded inside `return ( … );` puts the `;`
  // inside the parens, which is itself a syntax error and would make
  // isSingleExpression reject a genuine single-expression IIFE for the wrong
  // reason.
  const withoutTrailingSemis = trimmed.replace(/[;\s]+$/, '');

  // Anything after the function literal means the source does real work, so it is
  // not a single discarded expression. Parsing answers this exactly, where the
  // final character cannot.
  if (!isSingleExpression(withoutTrailingSemis)) return null;

  if (!withoutTrailingSemis.endsWith(')')) return FUNCTION_EXPRESSION_BODY_MESSAGE;

  // A leading "(" is ambiguous: its matching ")" might be the very last
  // character (pure grouping around the function literal — never invoked), or
  // there might be a call suffix after it (a real IIFE). Only the latter
  // executes.
  if (withoutTrailingSemis.startsWith('(')) {
    const closeIndex = findMatchingParenClose(withoutTrailingSemis, 0);
    if (closeIndex === withoutTrailingSemis.length - 1) {
      return FUNCTION_EXPRESSION_BODY_MESSAGE;
    }
  }

  return null;
}

/**
 * Truncate long agent-authored source so failure responses don't bloat the
 * stored-response payload with code the agent shouldn't retry verbatim.
 */
export function truncateForError(code: string, max = 500): string {
  if (code.length <= max) return code;
  return `${code.slice(0, max)}… (${code.length - max} chars truncated)`;
}

export function serializeRunCodeResult(result: unknown): string {
  if (result === undefined) {
    return '';
  }

  const seen = new WeakSet<object>();
  try {
    const serialized = JSON.stringify(result, (_key, value: unknown) => {
      if (typeof value === 'bigint') {
        return value.toString();
      }

      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }

      return value;
    });

    return serialized ?? String(result);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return `[Unserializable result: ${message}]`;
  }
}

/**
 * The decision the global dialog handler takes for a given dialog event.
 * Extracted so the idempotency guard + preset-action logic can be unit-tested
 * without a real Page/Dialog.
 */
export type DialogHandlerAction =
  | { kind: 'skip' }
  | { kind: 'accept'; promptText?: string }
  | { kind: 'dismiss-and-queue' }
  | { kind: 'dismiss-preset' };

export function decideDialogAction<D extends object>(
  dialog: D,
  handled: WeakSet<D>,
  nextAction: { action: 'accept' | 'dismiss'; promptText?: string } | null
): { action: DialogHandlerAction; clearNextAction: boolean } {
  if (handled.has(dialog)) return { action: { kind: 'skip' }, clearNextAction: false };
  handled.add(dialog);
  if (nextAction) {
    if (nextAction.action === 'accept') {
      return {
        action: { kind: 'accept', promptText: nextAction.promptText },
        clearNextAction: true,
      };
    }
    return { action: { kind: 'dismiss-preset' }, clearNextAction: true };
  }
  return { action: { kind: 'dismiss-and-queue' }, clearNextAction: false };
}

/**
 * Decide whether a `browser_close` request should be refused because closing
 * the current page would leave the session with no usable tab.
 *
 * Pure for unit testing — takes the current page URL and the URLs of all
 * other pages in the context, returns either `{ kind: 'allow' }` or
 * `{ kind: 'refuse', reason }`. A page URL is "usable" if it is not blank
 * (`about:blank`, `chrome://newtab`, empty string).
 *
 * Rationale: agents have reached for `browser_close` as a panic move when a
 * page is wedged on a native dialog. Closing the only live tab leaves the
 * session pinned to `about:blank`, which the agent cannot reliably navigate
 * away from (auth bootstrap, etc.) and burns the rest of the run trying.
 */
export function decideClosePolicy(
  currentUrl: string,
  otherPageUrls: readonly string[]
): { kind: 'allow' } | { kind: 'refuse'; reason: string } {
  const isBlank = (url: string): boolean =>
    url === '' || url === 'about:blank' || url.startsWith('chrome://newtab');
  const otherLive = otherPageUrls.filter((u) => !isBlank(u));
  if (otherLive.length > 0) return { kind: 'allow' };
  return {
    kind: 'refuse',
    reason:
      `browser_close refused: closing this page (${currentUrl || 'about:blank'}) would leave ` +
      `the session with no live tab. To recover from a wedged page, use browser_navigate to ` +
      `re-open the same URL instead. browser_close is for genuine multi-tab cleanup.`,
  };
}

export function buildListenerStripWarning(
  dialog: number,
  download: number,
  fileChooser = 0
): string {
  if (dialog === 0 && download === 0 && fileChooser === 0) return '';
  const parts: string[] = [];
  if (dialog > 0) parts.push(`${dialog} dialog listener${dialog === 1 ? '' : 's'}`);
  if (download > 0) parts.push(`${download} download listener${download === 1 ? '' : 's'}`);
  if (fileChooser > 0) {
    parts.push(`${fileChooser} file chooser listener${fileChooser === 1 ? '' : 's'}`);
  }
  return (
    `[Session] ${parts.join(' and ')} added by your code ` +
    `${dialog + download + fileChooser === 1 ? 'was' : 'were'} removed after this call. ` +
    `Dialog, download, and file upload handling is managed by the session — use the ` +
    `\`browser_handle_dialog\` and \`browser_file_upload\` tools instead of attaching listeners with ` +
    `\`page.on\` / \`page.once\` / \`page.waitForEvent\`.`
  );
}
